import type { ReactNode } from 'react';

/**
 * Arabic copy for this post.
 *
 * Every field is optional and falls back, field by field, to the English
 * literal in `page.tsx` / `layout.tsx`, so a half-authored record still
 * renders a complete page. Register: blended Najdi/Gulf for the body,
 * professional MSA for the SEO metadata. This is a competitor review, so the
 * Arabic stays specific and measured rather than snide — every criticism is
 * tied to the same evidence the English cites. Vendor names (Zendesk,
 * Intercom, Fin, Freshdesk, Open, Trustpilot, LinkedIn, Reddit, SOC 2, GDPR,
 * HIPAA, LLM, QA, CRM) stay Latin, and every figure, price and rating is left
 * exactly as written in the English.
 */

type PointAr = {
  title?: string;
  description?: string;
  details?: string[];
};

type ComplaintAr = {
  quote?: string;
  source?: string;
  context?: string;
};

type PricingRowAr = {
  provider?: string;
  baseCost?: string;
  aiCost?: string;
  notes?: string;
};

type IncidentAr = {
  date?: string;
  issue?: string;
  impact?: string;
  duration?: string;
};

type FaqAr = {
  question?: string;
  answer?: string;
};

export const AR: {
  // --- metadata (consumed by layout.tsx) ----------------------------------
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string[];
  ogImageAlt?: string;
  jsonLdHeadline?: string;
  jsonLdDescription?: string;
  jsonLdReviewBody?: string;
  jsonLdFaqs?: FaqAr[];

  // --- hero ---------------------------------------------------------------
  breadcrumbHome?: string;
  breadcrumbResources?: string;
  breadcrumbCurrent?: string;
  badge?: string;
  title?: string;
  subtitle?: string;
  byline?: string;
  updatedLabel?: string;
  readTime?: string;

  // --- verdict stats bar --------------------------------------------------
  statRatingLabel?: string;
  statPerResolutionLabel?: string;
  statTrustpilotLabel?: string;
  statParalysisLabel?: string;

  // --- table of contents --------------------------------------------------
  tocHeading?: string;
  tocTldr?: string;
  tocWhatIs?: string;
  tocGood?: string;
  tocBad?: string;
  tocCalculator?: string;
  tocComplaints?: string;
  tocComparison?: string;
  tocVerdict?: string;

  // --- tl;dr --------------------------------------------------------------
  tldrHeading?: string;
  tldrP1?: ReactNode;
  tldrGoodForTitle?: string;
  tldrGoodForItems?: string[];
  tldrBadForTitle?: string;
  tldrBadForItems?: string[];
  tldrBottomLine?: ReactNode;

  // --- what are they ------------------------------------------------------
  whatIsHeading?: string;
  whatIsP1?: string;
  whatIsP2?: string;
  claimsTitle?: string;
  claimsItems?: string[];
  reportsTitle?: string;
  reportsItems?: string[];

  // --- the good -----------------------------------------------------------
  goodHeading?: string;
  goodHeadingAside?: string;
  goodIntro?: string;
  goodPoints?: PointAr[];
  creditNote?: ReactNode;

  // --- the bad ------------------------------------------------------------
  badHeading?: string;
  badHeadingAside?: string;
  badIntro?: string;
  criticalBadge?: string;
  badPoints?: PointAr[];

  // --- incidents ----------------------------------------------------------
  incidentsHeading?: string;
  incidentsColDate?: string;
  incidentsColIssue?: string;
  incidentsColDuration?: string;
  incidentsColImpact?: string;
  serviceIncidents?: IncidentAr[];

  // --- calculator ---------------------------------------------------------
  calculatorHeading?: string;
  calculatorIntro?: string;
  calcTitle?: string;
  calcSubtitle?: string;
  calcResolutionsLabel?: string;
  calcTeamSizeLabel?: string;
  calcAgentsSuffix?: string;
  calcZendeskLabel?: string;
  calcIntercomLabel?: string;
  calcOpenLabel?: string;
  calcBaseWord?: string;
  calcAiWord?: string;
  calcResolutionsWord?: string;
  calcResolutionsOnly?: string;
  calcPerMonth?: string;
  calcSavingsLabel?: string;
  calcSavingsPerMonth?: string;
  calcSavingsPercentSuffix?: string;

  // --- complaints ---------------------------------------------------------
  complaintsHeading?: string;
  complaintsIntro?: ReactNode;
  customerComplaints?: ComplaintAr[];
  quoteOpen?: string;
  quoteClose?: string;
  patternTitle?: string;
  patternBody?: string;

  // --- pricing table ------------------------------------------------------
  pricingHeading?: string;
  pricingColProvider?: string;
  pricingColBase?: string;
  pricingColAi?: string;
  pricingColNotes?: string;
  bestValueBadge?: string;
  pricingComparison?: PricingRowAr[];

  // --- feature table ------------------------------------------------------
  comparisonHeading?: string;
  featureColFeature?: string;
  featureRows?: string[];
  legendFull?: string;
  legendPartial?: string;
  legendNone?: string;

  // --- verdict ------------------------------------------------------------
  verdictHeading?: string;
  ourRatingHeading?: string;
  verdictP1?: ReactNode;
  verdictP2?: string;
  verdictP3?: string;
  scoreValueLabel?: string;
  scoreFeaturesLabel?: string;
  scoreEaseLabel?: string;

  // --- faq + cta ----------------------------------------------------------
  faqHeading?: string;
  faqs?: FaqAr[];
  ctaHeading?: string;
  ctaBody?: string;
  ctaDemo?: string;
  ctaIntegration?: string;
} = {
  metaTitle: 'مراجعة وكلاء Zendesk الأذكياء: المزايا والأسعار والثغرات',
  metaDescription:
    'مراجعة صريحة ومعمّقة لوكلاء Zendesk الأذكياء. تحليل للأسعار، وشكاوى حقيقية من العملاء، ومقاييس أداء، وبدائل أفضل لعام 2026.',
  metaKeywords: [
    'وكلاء Zendesk الأذكياء',
    'مراجعة Zendesk AI',
    'أسعار Zendesk AI',
    'وكلاء Zendesk الأذكياء 2026',
    'شكاوى Zendesk AI',
    'بدائل Zendesk AI',
    'تكلفة Zendesk AI',
    'Zendesk مقابل Intercom',
    'Zendesk مقابل Open',
    'الذكاء الاصطناعي في خدمة العملاء',
    'مراجعة روبوت محادثة ذكي',
    'دعم العملاء بالذكاء الاصطناعي',
  ],
  ogImageAlt: 'مراجعة وكلاء Zendesk الأذكياء 2026',
  jsonLdHeadline: 'وكلاء Zendesk الأذكياء: المراجعة الكاملة (2026)',
  jsonLdDescription:
    'مراجعة صريحة ومعمّقة لوكلاء Zendesk الأذكياء. تحليل للأسعار، وشكاوى حقيقية من العملاء، ومقاييس أداء، وبدائل أفضل.',
  jsonLdReviewBody:
    'وكلاء Zendesk الأذكياء يقدّمون مزايا على مستوى المؤسسات، لكن بأسعار مرتفعة. تعقيد التطبيق، ومشكلات الهلوسة، وصعوبة التصعيد إلى موظف بشري تجعله خياراً صعباً لأغلب الشركات.',
  jsonLdFaqs: [
    {
      question: 'كم تكلفة وكلاء Zendesk الأذكياء؟',
      answer:
        'تبلغ تكلفة وكلاء Zendesk الأذكياء $1.50 لكل حل آلي، إضافة إلى رسوم المنصة الأساسية $55-150 لكل موظف شهرياً، إضافة إلى إضافات الذكاء الاصطناعي بـ$50-100 لكل موظف شهرياً. وقد تتجاوز التكلفة الإجمالية $20,000 شهرياً للفرق متوسطة الحجم.',
    },
    {
      question: 'هل يستحق Zendesk AI تكلفته؟',
      answer:
        'بالنسبة للمؤسسات الكبيرة المستثمرة أصلاً في Zendesk بعمق، ربما. أما لأغلب الشركات، فبدائل مثل Open أو Intercom Fin تقدّم قيمة أفضل مع إعداد أبسط، وتكلفة أقل لكل حل، وأداء مماثل أو أفضل.',
    },
  ],

  breadcrumbHome: 'الرئيسية',
  breadcrumbResources: 'المصادر',
  breadcrumbCurrent: 'مراجعة Zendesk AI',
  badge: 'مراجعة',
  title: 'مراجعة وكلاء Zendesk الأذكياء: المزايا والأسعار والثغرات',
  subtitle:
    'الحلو والمر والمكلف. نظرة صريحة على وكلاء Zendesk الأذكياء: وش يتقنونه، ووين يقصّرون، وهل يستاهلون السعر المرتفع.',
  byline: 'بقلم فريق Open',
  updatedLabel: 'آخر تحديث',
  readTime: '18 دقيقة قراءة',

  statRatingLabel: 'تقييمنا',
  statPerResolutionLabel: 'لكل حل (مرتفع)',
  statTrustpilotLabel: 'تقييم Trustpilot',
  statParalysisLabel: 'من القادة يبلّغون عن شلل في القرار',

  tocHeading: 'في هذي المراجعة',
  tocTldr: 'الخلاصة السريعة',
  tocWhatIs: 'وش هم وكلاء Zendesk الأذكياء؟',
  tocGood: 'الإيجابيات (باختصار)',
  tocBad: 'السلبيات (بالتفصيل)',
  tocCalculator: 'حاسبة التكلفة',
  tocComplaints: 'شكاوى عملاء حقيقية',
  tocComparison: 'مقارنة المزايا',
  tocVerdict: 'الحكم النهائي',

  tldrHeading: 'الخلاصة السريعة: تجاوزها إذا تبغى التفاصيل',
  tldrP1: (
    <>
      <strong>وكلاء Zendesk الأذكياء أقوياء لكن فيهم إشكالات.</strong> يقدّمون
      مزايا على مستوى المؤسسات، وتكاملاً عميقاً مع Zendesk، ودعماً لكل القنوات،
      لكن بتكلفة صعب تبريرها.
    </>
  ),
  tldrGoodForTitle: 'مناسب لـ',
  tldrGoodForItems: [
    '• المؤسسات الكبيرة الموجودة أصلاً على Zendesk',
    '• الفرق اللي عندها موارد مخصصة للتطبيق',
    '• الشركات اللي تحتاج امتثالاً صارماً',
  ],
  tldrBadForTitle: 'غير مناسب لـ',
  tldrBadForItems: [
    '• الفرق اللي تراقب الميزانية (التكاليف تتراكم بسرعة)',
    '• الشركات الناشئة سريعة الحركة (التطبيق بطيء)',
    '• أي أحد يبغى ذكاءً اصطناعياً بسيطاً وموثوقاً',
  ],
  tldrBottomLine: (
    <>
      <strong>الخلاصة:</strong> بسعر $1.50 لكل حل + تكاليف مقاعد مرتفعة، وكلاء
      Zendesk الأذكياء أغلى بنسبة 50%+ من بدائل مثل Open ($0.99 لكل حل، وبدون
      مقاعد) أو Intercom Fin ($0.99 لكل حل)، وغالباً بأداء أضعف على أرض الواقع.
    </>
  ),

  whatIsHeading: 'وش هم وكلاء Zendesk الأذكياء؟',
  whatIsP1:
    'وكلاء Zendesk الأذكياء هم رد الشركة على ثورة الذكاء الاصطناعي التوليدي. مبنيين على ما تسميه Zendesk تقنية «الذكاء الاصطناعي الوكيلي»، ومصمّمين عشان يتعاملون مع استفسارات العملاء بشكل مستقل عبر كل القنوات: الدردشة والإيميل والتواصل الاجتماعي وWhatsApp، وحتى الصوت.',
  whatIsP2:
    'وعلى خلاف روبوتات المحادثة التقليدية المبنية على القواعد، وكلاء Zendesk الأذكياء يقدرون (نظرياً) يفكّرون في المشكلات المعقّدة، ويحافظون على سياق المحادثة، وينفّذون إجراءات حقيقية مثل معالجة المبالغ المستردة أو تحديث الحسابات.',
  claimsTitle: 'وش تدّعيه Zendesk',
  claimsItems: [
    'حل أكثر من 80% من تفاعلات العملاء',
    'التشغيل خلال دقائق وبدون تدريب',
    'تسعير قائم على النتائج (تدفع مقابل النتيجة)',
    'استدلال وكيلي للمشكلات المعقّدة',
  ],
  reportsTitle: 'وش يقوله المستخدمون',
  reportsItems: [
    'نسب أتمتة فعلية بين 40-60%',
    'شهور من العمل على التطبيق',
    'تكاليف خفية تتضخم بسرعة',
    'هلوسات وإجابات خاطئة',
  ],

  goodHeading: 'الإيجابيات',
  goodHeadingAside: '(من باب الإنصاف)',
  goodIntro:
    'Zendesk مو كلها سلبيات. بنوا منصة شاملة فيها نقاط قوة حقيقية. هذا اللي يتقنونه:',
  goodPoints: [
    {
      title: 'تغطية كل القنوات',
      description:
        'وكلاء Zendesk الأذكياء يشتغلون عبر الإيميل والدردشة والتواصل الاجتماعي وWhatsApp، وحتى الصوت (مع Voice AI). وكيل واحد يتعامل مع كل القنوات بنفس الاتساق.',
    },
    {
      title: 'تكامل عميق مع Zendesk',
      description:
        'إذا أنت أصلاً على Zendesk، الوكلاء الأذكياء يتكاملون بسلاسة مع تذاكرك وماكروهاتك ومركز المساعدة وسير العمل الموجود عندك. وما تحتاج تقلع النظام وتركّب غيره.',
    },
    {
      title: 'أمان بمستوى المؤسسات',
      description:
        'امتثال SOC 2، وGDPR، وخيارات HIPAA. وضوابط لموقع تخزين البيانات. عملاء المؤسسات يحصلون على البنود الأمنية اللي يحتاجونها.',
    },
    {
      title: 'أدوات ضمان الجودة',
      description:
        'Zendesk QA ودرجات الجودة الآلية تخليك تراقب أداء الذكاء الاصطناعي، وتقارنه بالموظفين البشر، وتلقط المشكلات قبل العملاء.',
    },
  ],
  creditNote: (
    <>
      <strong>الحق يُقال:</strong> Zendesk كانت سبّاقة في كثير من ابتكارات خدمة
      العملاء، ولا تزال في المقدمة في دعم المؤسسات. وإذا كنت شركة من Fortune 500
      بميزانية مفتوحة وجيش من مستشاري التطبيق، يمكن وكلاؤهم الأذكياء يناسبونك.
      وللبقية؟ كمّل قراءة.
    </>
  ),

  badHeading: 'السلبيات',
  badHeadingAside: '(وهذا اللي جابك هنا)',
  badIntro:
    'الحين نجي للجزء المهم. بناءً على مراجعات المستخدمين، ومنتديات المجتمع، وتحليلات القطاع، واختبارنا الخاص، هذا وش الخلل في وكلاء Zendesk الأذكياء:',
  criticalBadge: 'حرِج',
  badPoints: [
    {
      title: 'أسعار موجعة',
      description:
        'بسعر $1.50 لكل حل آلي (زائد $55-150 لكل موظف شهرياً كأساس)، التكاليف تتصاعد بسرعة. شركة عندها 10,000 حل شهرياً تدفع $15,000 شهرياً على الذكاء الاصطناعي وحده، فوق تكلفة المقاعد.',
      details: [
        'الخطط الأساسية تبدأ من $55 لكل موظف شهرياً (Team) وتوصل $150+ لكل موظف شهرياً (Enterprise)',
        'إضافات الذكاء الاصطناعي: $50-100 لكل موظف شهرياً زيادة',
        'التسعير لكل حل: $1.50 لكل حل آلي',
        'منافسون مثل Intercom يأخذون $0.99 لكل حل، أي أرخص بـ33%',
      ],
    },
    {
      title: 'كابوس التطبيق',
      description:
        'رغم الادعاءات التسويقية بـ«بدون تدريب»، التطبيق الواقعي يحتاج فرقاً مخصصة، وشهوراً من الإعداد، وضبطاً مستمراً. و62% من قادة تجربة العميل يبلّغون عن شلل في القرار حول تبنّي Zendesk AI.',
      details: [
        'يحتاج قائد مشروع، وبُناة للذكاء الاصطناعي، ومسؤول CRM، ومهندسي تكامل',
        'محتوى مركز المساعدة لازم يتحسّن قبل ما يشتغل الذكاء الاصطناعي عدل',
        'التكاملات المخصصة تتحول إلى «مشكلات تمتد شهوراً»',
        'أنظمة الذكاء الاصطناعي غير الحتميّة ما تنسجم مع الجداول الزمنية التقليدية للتطوير',
      ],
    },
    {
      title: 'هلوسة وإجابات خاطئة',
      description:
        'منتديات مجتمع Zendesk نفسها مليانة شكاوى من ذكاء اصطناعي يعطي معلومات غير صحيحة بثقة، وروابط مكسورة، وصفحات تواصل قديمة.',
      details: [
        'الذكاء الاصطناعي يوجّه العملاء إلى صفحات دعم غير موجودة',
        'المحادثات متعددة الجولات تخفق لما يوضّح العميل أو يقاطع',
        'الأسئلة الغامضة تولّد ردوداً واثقة لكنها خاطئة',
        'الربط بقاعدة المعرفة يساعد، لكنه ما يلغي المشكلة',
      ],
    },
    {
      title: 'التصعيد للموظف البشري ثقب أسود',
      description:
        'لما ما يقدر الذكاء الاصطناعي يساعد (وهذا يصير كثير)، العملاء يبلّغون عن صعوبة بالغة في الوصول لموظف بشري. الوكيل يتحوّل إلى جدار بدل ما يكون مساعداً.',
      details: [
        '«ما يوصلونك أبداً لوين تبغى» (شكوى متكررة من المستخدمين)',
        'الذكاء الاصطناعي ما يقدر يعالج مشكلات خاصة بالحساب، ومع ذلك ما يحوّل بشكل صحيح',
        'إيجاد وسائل تواصل بديلة صعب عن قصد',
        'العملاء ينضطرون يبدون «من الصفر» لما يوصلون أخيراً لموظف بشري',
      ],
    },
    {
      title: 'مراجعة واقعية لنسبة الحل',
      description:
        'Zendesk تدّعي «أتمتة 80%»، لكن الاختبارات المستقلة تحكي قصة ثانية. Fin من Intercom تفوّق على Zendesk AI في 80% من حالات الاختبار.',
      details: [
        'ادعاءات تسويقية بأتمتة 80% مقابل 40-60% على أرض الواقع',
        'Intercom Fin يتعامل مع «ضِعف عدد الأسئلة المعقّدة»',
        'مؤشر «المحادثات المفهومة» يضخّم الأداء الظاهري',
        'الدقة في الجولة الواحدة ما تترجم إلى نجاح عبر جولات متعددة',
      ],
    },
    {
      title: 'تعقيد المنصة وتضخّمها',
      description:
        'Zendesk صارت منصة متشعّبة تحتاج تطبيقات طرف ثالث لوظائف أساسية. والواجهة تحس فيها قديمة مقارنة بالبدائل الحديثة.',
      details: [
        'تجربة مجزّأة تتطلب تطبيقات من المتجر',
        'تكامل محدود مع Slack وTeams بشكل أصلي',
        'الواجهة ما واكبت معايير التصميم الحديثة',
        'تضخّم المزايا يخلي المهام البسيطة معقّدة',
      ],
    },
  ],

  incidentsHeading: 'أعطال الخدمة الأخيرة (2025)',
  incidentsColDate: 'التاريخ',
  incidentsColIssue: 'المشكلة',
  incidentsColDuration: 'المدة',
  incidentsColImpact: 'الأثر',
  serviceIncidents: [
    {
      date: '1-5 يونيو 2025',
      issue: 'بطء استجابة الوكلاء الأذكياء (uGPT)',
      impact: 'تأثّر 15% من المحادثات',
      duration: '4+ أيام',
    },
    {
      date: '23 أبريل 2025',
      issue: 'تأخّر تحديث التذاكر وأعداد غير دقيقة',
      impact: 'عملاء Pod 17',
      duration: '~ساعتان',
    },
  ],

  calculatorHeading: 'حاسبة التكلفة التفاعلية',
  calculatorIntro:
    'خلّنا نسوي الحساب. حرّك المؤشرات وشوف كم بتكلّف وكلاء Zendesk الأذكياء فريقك فعلاً، وكم توفّر مع البدائل.',
  calcTitle: 'حاسبة تكلفة Zendesk AI',
  calcSubtitle: 'شوف كم بتدفع فعلاً',
  calcResolutionsLabel: 'الحلول الآلية شهرياً',
  calcTeamSizeLabel: 'حجم فريق الدعم',
  calcAgentsSuffix: 'موظف',
  calcZendeskLabel: 'Zendesk AI (الحزمة الكاملة)',
  calcIntercomLabel: 'Intercom + Fin',
  calcOpenLabel: 'Open (بدون رسوم على كل موظف)',
  calcBaseWord: 'أساسي',
  calcAiWord: 'ذكاء اصطناعي',
  calcResolutionsWord: 'حلول',
  calcResolutionsOnly: 'حلول فقط',
  calcPerMonth: '/شهر',
  calcSavingsLabel: 'بتوفّر مع Open',
  calcSavingsPerMonth: '/شهر',
  calcSavingsPercentSuffix: 'أقل من Zendesk',

  complaintsHeading: 'وش يقول المستخدمون فعلاً',
  complaintsIntro: (
    <>
      وهذي مو حالات منتقاة. Zendesk عندها{' '}
      <strong>تقييم 1.8/5 على Trustpilot</strong>. وهذا اللي يعيشه العملاء:
    </>
  ),
  customerComplaints: [
    {
      quote:
        'الذكاء الاصطناعي وجّهني لصفحة تواصل قديمة من Zendesk ما عادت موجودة. علقت في دوامة وما لقيت أي طريقة أوصل فيها للدعم.',
      source: 'Gergely Orosz، LinkedIn',
      context: 'عميل يدفع لـZendesk',
    },
    {
      quote:
        'ما يوصلونك أبداً لوين تبغى، وهم أقرب لمصدر إزعاج وعائق. أفضّل واجد أتكلم مع إنسان حقيقي.',
      source: 'مشارك في استطلاع قطاعي',
      context: 'عن تجارب روبوتات المحادثة الذكية',
    },
    {
      quote:
        'الروبوت يسرد محتوى أسئلة شائعة ما له علاقة، ويكرر نفسه، وما يفهم السياق. يرجّعني لنقطة الصفر كل مرة.',
      source: 'مستخدم على Reddit',
      context: 'نقاش حول دعم العملاء',
    },
  ],
  quoteOpen: '«',
  quoteClose: '»',
  patternTitle: 'النمط المتكرر',
  patternBody:
    'لاحظت الخيط المشترك؟ ذكاء اصطناعي ما يقدر يساعد، وما يحوّل للبشر، ويخلي العملاء عالقين. هذا ذكاء اصطناعي يسدّ طريق الدعم بدل ما يعزّزه.',

  pricingHeading: 'مقارنة الأسعار',
  pricingColProvider: 'المزوّد',
  pricingColBase: 'التكلفة الأساسية',
  pricingColAi: 'تكلفة الذكاء الاصطناعي',
  pricingColNotes: 'ملاحظات',
  bestValueBadge: 'أفضل قيمة',
  pricingComparison: [
    {
      baseCost: '$55-150 لكل موظف/شهر',
      aiCost: '$1.50 لكل حل',
      notes: 'زائد $50-100 لكل موظف كإضافة ذكاء اصطناعي',
    },
    {
      baseCost: 'متغيّرة',
      aiCost: '$0.99 لكل حل',
      notes: 'أرخص بـ33% لكل حل',
    },
    {
      baseCost: 'مشمولة',
      aiCost: '$0.99 لكل حل',
      notes: 'بدون رسوم على كل موظف، تدفع مقابل النتائج فقط',
    },
    {
      baseCost: 'مجاناً حتى $79 لكل موظف/شهر',
      aiCost: 'مشمولة في الخطط',
      notes: 'بديل أقل تكلفة',
    },
  ],

  comparisonHeading: 'مقارنة المزايا',
  featureColFeature: 'الميزة',
  featureRows: [
    'التسعير لكل حل',
    'بدون رسوم على كل موظف',
    'فهم حقيقي قائم على LLM',
    'إعداد سهل (أقل من يوم)',
    'دعم كل القنوات',
    'تسليم سلس للموظف البشري',
    'ذكاء اصطناعي صوتي',
    'يشتغل مع نظام الدعم الحالي',
  ],
  legendFull: 'دعم كامل',
  legendPartial: 'جزئي',
  legendNone: 'غير متوفر',

  verdictHeading: 'الحكم النهائي',
  ourRatingHeading: 'تقييمنا',
  verdictP1: (
    <>
      <strong>
        وكلاء Zendesk الأذكياء منتج بمواصفات متميزة وأسعار متميزة، بدون نتائج
        متميزة.
      </strong>
    </>
  ),
  verdictP2:
    'يشتغلون بأفضل صورة مع المؤسسات الكبيرة المستثمرة أصلاً بعمق في منظومة Zendesk، واللي عندها فرق تطبيق مخصصة وميزانيات تستوعب التكلفة. ولهذي المؤسسات، التكامل الوثيق ومزايا الأمان المؤسسية ممكن تبرّر السعر الأعلى.',
  verdictP3:
    'ولغيرهم (الشركات الناشئة، والشركات الصغيرة والمتوسطة، والفرق اللي تحتاج ذكاءً اصطناعياً يشتغل من أول يوم) في خيارات أفضل. Open تقدّم قدرات ذكاء اصطناعي مماثلة بتكلفة أقل بـ50%+ وبدون رسوم على كل موظف. وIntercom Fin يقدّم أداءً أفضل بحسب الاختبارات المستقلة.',
  scoreValueLabel: 'القيمة مقابل السعر',
  scoreFeaturesLabel: 'المزايا',
  scoreEaseLabel: 'سهولة الاستخدام',

  faqHeading: 'الأسئلة الشائعة',
  faqs: [
    {
      question: 'كم تكلّف وكلاء Zendesk الأذكياء فعلاً؟',
      answer:
        'التكلفة الإجمالية تشمل: المنصة الأساسية ($55-150 لكل موظف شهرياً)، وإضافات الذكاء الاصطناعي ($50-100 لكل موظف شهرياً)، ورسوم لكل حل ($1.50 لكل حل). فريق من 10 موظفين مع 5,000 حل ذكي شهرياً يدفع تقريباً $9,250 في الشهر. يعني $111,000 في السنة قبل أي رسوم تجاوز.',
    },
    {
      question: 'هل Zendesk AI أفضل من Intercom Fin؟',
      answer:
        'الاختبارات المستقلة تبيّن إن Intercom Fin يتفوّق على Zendesk AI في 80% من حالات الاختبار، ويتعامل مع ضِعف عدد الأسئلة المعقّدة. وFin بعد أقل تكلفة ($0.99 لكل حل مقابل $1.50). وإذا ما كنت متكاملاً بعمق مع Zendesk أصلاً، فFin عادةً الخيار الأفضل.',
    },
    {
      question: 'هل يقدر وكلاء Zendesk الأذكياء يهلوسون؟',
      answer:
        'نعم. مثل كل الأنظمة القائمة على LLM، Zendesk AI ممكن يعطي معلومات غير صحيحة بثقة. المستخدمون يبلّغون عن توجيههم لصفحات غير موجودة، وتلقّي معلومات خاطئة عن السياسات، والعلوق في دوامات. وأدوات QA من Zendesk تساعد في لقط المشكلات، لكنها ما تلغيها.',
    },
    {
      question: 'كم ياخذ تطبيق Zendesk AI من وقت؟',
      answer:
        'رغم التسويق بـ«بدون تدريب»، التطبيق الواقعي عادةً ياخذ أسابيع إلى شهور. تحتاج تحسّن محتوى مركز المساعدة، وتضبط التكاملات، وتعدّ إجراءات العمل، وتضبط الذكاء الاصطناعي. وZendesk نفسها توصي بتكوين فريق مخصص يضم قائد مشروع وبُناة للذكاء الاصطناعي ومسؤول CRM.',
    },
    {
      question: 'وش أفضل بديل لـZendesk AI؟',
      answer:
        'لأداء الذكاء الاصطناعي البحت: Intercom Fin. وللقيمة (أداء + سعر): Open، اللي يقدّم $0.99 لكل حل بدون رسوم على كل موظف، ويشتغل مع نظام الدعم الحالي عندك، ويحقق أتمتة 77%. وللفرق اللي تراقب الميزانية: Freshdesk يقدّم باقة مجانية بذكاء اصطناعي أساسي.',
    },
    {
      question: 'هل أنتقل من Zendesk؟',
      answer:
        'يعتمد على وضعك. إذا كنت مؤسسة بتكامل عميق مع Zendesk، تكاليف الانتقال ممكن تفوق الفائدة. لكن إذا كنت متردداً، أو تدفع أكثر من اللازم، أو محبطاً من أداء الذكاء الاصطناعي، فبدائل مثل Open تقدر تشتغل فوق Zendesk (بدون ما تقلع النظام وتركّب غيره) وتعطيك ذكاءً اصطناعياً أفضل بتكلفة أقل.',
    },
  ],
  ctaHeading: 'تبغى ذكاءً اصطناعياً يشتغل بدون سعر Zendesk؟',
  ctaBody:
    'Open تحقق أتمتة 77% بسعر $0.99 لكل حل، بدون رسوم على كل موظف وبدون كوابيس تطبيق. وتشتغل مع نظام الدعم الحالي عندك.',
  ctaDemo: 'شوف Open وهي تشتغل',
  ctaIntegration: 'التكامل مع Zendesk',
};
